/**
 * dev-env-api-core.js — pure, alias-free client core for the cluster-api
 * Dev Environments API (P6, `/api/cluster/dev-environments`).
 *
 * The real axios instance lives in `services/dev-environments-api.js`; this
 * module only knows the HTTP verb/URL/params/body contract and the envelope
 * semantics, so `node --test` can exercise it with a fake `http` object.
 *
 * Envelope shapes we have to survive (routes/dev_environments.py):
 *   - success:            { success:true, message, data, count? }
 *   - service error:      { success:false, message, status_code, errors:[{error:<code>, ...}] }
 *   - feature flag OFF:   BARE 503 { error:"dev_environments_disabled" }
 *   - permission denied:  BARE 403 { error:"forbidden", resource, action }
 *   - auth-api down:      BARE 503 { error:"service_unavailable", ... }
 *   - per-document deny:  BARE 403 { error:"forbidden", resource, reason:"not_owner"|"create_not_permitted" }
 *
 * No `@/` imports — keep this module pure.
 */

const BASE = '/cluster/dev-environments'

function enc(v) {
  return encodeURIComponent(String(v))
}

/** Drop undefined/null/'' query params (mirrors utsApi.listInstances). */
export function cleanParams(params) {
  const clean = {}
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') clean[k] = v
  })
  return clean
}

/**
 * Unwrap the unified response shape — identical semantics to the `unwrap()`
 * in cluster-api.js / uts-api.js: throw a descriptive Error (with `.response`
 * attached) when `success === false`, return `data` otherwise, and pass bare
 * bodies straight through.
 */
export function unwrapEnvelope(response) {
  const body = response?.data
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      const err = new Error(body.message || 'Request failed')
      err.response = response
      throw err
    }
    return body.data
  }
  return body
}

/**
 * Build the Dev Environments client over an axios-like `http`
 * (`get/post/delete` returning `{ status, data }`). `unwrap` is injectable so
 * a service can hand in its own; defaults to `unwrapEnvelope`.
 *
 * NOTE `workspace()` bumps `lifecycle.last_active_at` server-side on EVERY
 * call — it is a user-intent signal ("open VS Code / shell"), never a poll.
 */
export function createDevEnvironmentsApi(http, { unwrap = unwrapEnvelope } = {}) {
  if (!http) throw new Error('createDevEnvironmentsApi: http client is required')
  return {
    /** `GET /cluster/dev-environments?…` → array of public env docs. */
    async list(params = {}) {
      const response = await http.get(BASE, { params: cleanParams(params) })
      return unwrap(response) || []
    },

    /** `GET /cluster/dev-environments/<id>?live=1` → public env doc. */
    async get(id, { live = 1 } = {}) {
      const response = await http.get(`${BASE}/${enc(id)}`, { params: { live } })
      return unwrap(response)
    },

    /**
     * `POST /cluster/dev-environments` → `{ status, data }`. The HTTP status is
     * load-bearing: 201 = created/adopted, 200 = an existing env was returned.
     * `data` = `{ environment, existing, adopted, dev_workspace? }`.
     */
    async create(body) {
      const response = await http.post(BASE, body || {})
      return { status: response?.status, data: unwrap(response) }
    },

    /** `POST /cluster/dev-environments/<id>/start` → `{ environment, dev_workspace }`. */
    async start(id, enable) {
      const body = enable && typeof enable === 'object' ? { enable } : {}
      const response = await http.post(`${BASE}/${enc(id)}/start`, body)
      return unwrap(response)
    },

    /** `POST /cluster/dev-environments/<id>/stop` → `{ environment, status }`. */
    async stop(id) {
      const response = await http.post(`${BASE}/${enc(id)}/stop`, {})
      return unwrap(response)
    },

    /** `DELETE /cluster/dev-environments/<id>` → `{ deleted, stopped_first, environment_id }`. */
    async remove(id) {
      const response = await http.delete(`${BASE}/${enc(id)}`)
      return unwrap(response)
    },

    /** `GET /cluster/dev-environments/<id>/workspace` — see the NOTE above. */
    async workspace(id) {
      const response = await http.get(`${BASE}/${enc(id)}/workspace`)
      return unwrap(response)
    },

    /**
     * `GET /cluster/dev-environments/<id>/connection-info` — the APPLICATION's
     * connection info (the same installed-app builder behind the instance
     * page's modal), NOT the workspace: it never bumps `last_active_at`.
     * Returns `{ info, meta }`: `info` is the builder payload verbatim (per-
     * template sections, `services`/`ingresses`, `dns_configuration`,
     * `credentials`, `connectivity_status`, `pods`…), `meta` is the envelope's
     * `{ cluster, namespace, template_id, workload, scope }` (`null` when the
     * server sent none). 409 `env_not_active` while stopped/failed; 502
     * `connection_info_failed` when the cluster cannot be read.
     *
     * Since cluster-api 2.25.43 the server masks credential values itself and
     * reports what it did in `info.secret_masking`. Pass `{ reveal: true }` to
     * ask for clear values — the param is sent ONLY when asked for, and the
     * server answers it only for a caller who passes an instance-scoped
     * `secret:viewer` check (403 `secret_reveal_forbidden` otherwise). Never
     * request a reveal speculatively: each granted reveal is audited.
     */
    async connectionInfo(id, { reveal = false } = {}) {
      const url = `${BASE}/${enc(id)}/connection-info`
      // No `?reveal` at all on the normal read — the masked payload is the
      // default and the query param is the explicit, audited ask.
      const response = reveal ? await http.get(url, { params: { reveal: 1 } }) : await http.get(url)
      const info = unwrap(response)
      const body = response && response.data
      const meta = body && typeof body === 'object' && body.meta && typeof body.meta === 'object' ? body.meta : null
      return { info: info && typeof info === 'object' ? info : {}, meta }
    },

    // ── Developer loop Stage A — git inside the workspace (exec, never SSH) ──
    // These are NOT polled: the store loads them on card mount, on an explicit
    // Refresh, and after a push.

    /**
     * `GET /cluster/dev-environments/<id>/git/status` →
     * `{ branch, upstream, ahead, behind, clean, changed_count,
     *    changed_files:[{code,path}], last_commit|null, remote_host, detached }`.
     */
    async gitStatus(id) {
      const response = await http.get(`${BASE}/${enc(id)}/git/status`)
      return unwrap(response)
    },

    /**
     * `GET /cluster/dev-environments/<id>/git/diff[?path=]` →
     * `{ diff, truncated, stat, path }`. `path` is repo-relative; omitted/empty
     * = the whole tree (the param is dropped, not sent blank).
     */
    async gitDiff(id, path) {
      const response = await http.get(`${BASE}/${enc(id)}/git/diff`, { params: cleanParams({ path }) })
      return unwrap(response)
    },

    /**
     * `POST /cluster/dev-environments/<id>/git/commit-push`
     * body `{ message, add_all?, paths?, branch? }` →
     * `{ committed, pushed, sha, short_sha, branch, console, identity }`.
     * `message` is always sent; `paths` only when it is a non-empty array
     * (the server then stages just those files); `add_all` / `branch` only
     * when the caller sets them.
     */
    async gitCommitPush(id, body = {}) {
      const b = body && typeof body === 'object' ? body : {}
      const payload = { message: typeof b.message === 'string' ? b.message : '' }
      if (Array.isArray(b.paths) && b.paths.length) payload.paths = b.paths.map(String)
      if (typeof b.add_all === 'boolean') payload.add_all = b.add_all
      if (typeof b.branch === 'string' && b.branch.trim()) payload.branch = b.branch.trim()
      const response = await http.post(`${BASE}/${enc(id)}/git/commit-push`, payload)
      return unwrap(response)
    },

    // ── Developer loop Stage B — release (pushed → pipeline → deployed) ──
    // Built from GitLab + the cluster + the env record; NEVER touches the
    // workspace, so it is safe to poll (the Release card polls it 6 s → 15 s
    // while a pipeline or rollout is in flight).

    /**
     * `GET /cluster/dev-environments/<id>/release/status[?sha=]` →
     * `{ kind, sha, short_sha, pushed_at, branch, pipeline|null, pipeline_error?,
     *    deployed:{state, live_ref, detail}, steps:[{key,state,at?,detail?}],
     *    deploy_restarts_environment, live_image, release? }`.
     * `sha` defaults server-side to `git.last_push.sha` (409 `nothing_pushed`
     * when there is none); an empty/absent `sha` is dropped, not sent blank.
     */
    async releaseStatus(id, sha) {
      const response = await http.get(`${BASE}/${enc(id)}/release/status`, { params: cleanParams({ sha }) })
      return unwrap(response)
    },

    /**
     * `POST /cluster/dev-environments/<id>/release/adopt` body `{ new_tag?, note? }`
     * (catalog only) → `{ template_id, container, current_tag, tag, note,
     * pipeline:{id,status,web_url,ref,created_at}, release }`. Blank strings
     * are dropped so the server picks the default (next semver).
     */
    async releaseAdopt(id, body = {}) {
      const b = body && typeof body === 'object' ? body : {}
      const payload = {}
      if (typeof b.new_tag === 'string' && b.new_tag.trim()) payload.new_tag = b.new_tag.trim()
      if (typeof b.note === 'string' && b.note.trim()) payload.note = b.note.trim()
      const response = await http.post(`${BASE}/${enc(id)}/release/adopt`, payload)
      return unwrap(response)
    },

    /**
     * `POST /cluster/dev-environments/<id>/release/finalize` body
     * `{ adopt, exit_dev_mode, new_tag?, note?, pipeline_id? }` (catalog only) →
     * `{ registered, adopted, exited_dev_mode, tag, should_redeploy,
     *    environment, adopt_error? }`. `adopt` defaults to true and
     * `exit_dev_mode` to false — both are ALWAYS sent so the request says what
     * it means.
     */
    async releaseFinalize(id, body = {}) {
      const b = body && typeof body === 'object' ? body : {}
      const payload = {
        adopt: b.adopt === undefined || b.adopt === null ? true : Boolean(b.adopt),
        exit_dev_mode: Boolean(b.exit_dev_mode)
      }
      if (typeof b.new_tag === 'string' && b.new_tag.trim()) payload.new_tag = b.new_tag.trim()
      if (typeof b.note === 'string' && b.note.trim()) payload.note = b.note.trim()
      if (b.pipeline_id !== undefined && b.pipeline_id !== null && b.pipeline_id !== '') payload.pipeline_id = b.pipeline_id
      const response = await http.post(`${BASE}/${enc(id)}/release/finalize`, payload)
      return unwrap(response)
    }
  }
}

/**
 * Normalise any thrown request error into `{ code, status, message, extra }`.
 *   code    — `body.error` (bare bodies) ?? `body.errors[0].error` (envelopes) ?? null
 *   status  — HTTP status (undefined on a network failure)
 *   message — `body.message` ?? err.message
 *   extra   — `errors[0]` for envelopes; for BARE bodies the body itself, so
 *             `reason` / `resource` on a 403 stay reachable (the spec's
 *             `errors[0]` and the bare body are the same "detail" concept).
 */
export function apiErrorCode(err) {
  const resp = err && err.response
  const status = resp ? resp.status : undefined
  const body = resp && resp.data && typeof resp.data === 'object' ? resp.data : {}
  const first = Array.isArray(body.errors) && body.errors.length ? body.errors[0] : null
  const firstObj = first && typeof first === 'object' ? first : null
  let code = null
  if (typeof body.error === 'string' && body.error) code = body.error
  else if (firstObj && typeof firstObj.error === 'string') code = firstObj.error
  const extra = firstObj || (code && !('success' in body) ? body : null)
  const message =
    (typeof body.message === 'string' && body.message) ||
    (firstObj && typeof firstObj.message === 'string' && firstObj.message) ||
    (err && err.message) ||
    'Request failed'
  return { code, status, message, extra }
}

/**
 * Capability probe classification for the section gate.
 *   'available'       — no error
 *   'disabled'        — 503 + dev_environments_disabled (feature flag off)
 *   'unavailable'     — 503 (service_unavailable / anything else) or a network failure
 *   'forbidden'       — 403 bare forbidden WITHOUT a per-document `reason`
 *   'unauthenticated' — 401
 *   'error'           — anything else (incl. a 403 that carries a `reason`)
 */
export function classifyGate(err) {
  if (!err) return 'available'
  const { code, status, extra } = apiErrorCode(err)
  if (status === undefined || status === null) return 'unavailable'
  if (status === 503) return code === 'dev_environments_disabled' ? 'disabled' : 'unavailable'
  if (status === 401) return 'unauthenticated'
  if (status === 403) {
    const reason = extra && extra.reason
    if ((code === 'forbidden' || code === null) && !reason) return 'forbidden'
    return 'error'
  }
  return 'error'
}
